// src/advisories.js
// Embeddable, CMS-agnostic travel-advisories board. Auto-mounts into any
// [data-visamundi-advisories] container, fetches the flattened Directus
// referential (5 foreign ministries: US · CA · DE · GB · FR) from the
// advisories endpoint, groups it by destination (headline level = average of
// the ministries' ratings, rounded — see src/group.js), and renders rounded
// flags, colour-coded normalized threat levels (1–4) and per-ministry badges
// inside an isolated shadow root.
//
// Parameters (data-attributes on the container):
//   data-endpoint   API URL (default https://advisories.visamundi.app/api/advisories)
//   data-lang       "en" | "fr"                    (default "en")
//   data-theme      "light" | "dark"               (default "light")
//   data-limit      max destinations shown         (default 24)
//   data-min-level  1–4 — preselects the "≥ N" filter chip (default: all)
//   data-source     US | CA | DE | GB | FR — only that ministry's advisories
//   data-consensus  present/"true" — only destinations where every covering
//                   ministry (min. 4) rates at least the active level (≥ 2)
import styles from './styles.css';
import { SOURCE_ORDER, groupByDestination } from './group.js';

// Default to the absolute endpoint so the script works embedded on any origin
// (a relative /api/advisories would hit the host page's domain). Override with
// data-endpoint for self-hosting / a deploy preview.
const DEFAULT_ENDPOINT = 'https://advisories.visamundi.app/api/advisories';

const I18N = {
  en: {
    title: 'Travel advisories',
    intro: 'Live from 5 foreign ministries — US · CA · DE · GB · FR',
    introSingle: 'Live from the',
    loading: 'Loading the latest advisories…',
    error: 'Couldn’t load advisories right now.',
    unavailable: 'unavailable',
    destinations: 'destinations',
    empty: 'No advisories match this filter.',
    all: 'All',
    level: 'Level',
    read: 'Read advisory →',
    consensus: (n) => 'Consensus of ' + n + ' MFAs',
    levels: {
      1: 'Exercise normal precautions',
      2: 'Exercise increased caution',
      3: 'Reconsider travel',
      4: 'Do not travel',
    },
    sources: { US: 'U.S. MFA', CA: 'Canada MFA', DE: 'Germany MFA', GB: 'UK MFA', FR: 'France MFA' },
  },
  fr: {
    title: 'Conseils aux voyageurs',
    intro: 'En direct de 5 ministères des affaires étrangères — US · CA · DE · GB · FR',
    introSingle: 'En direct du',
    loading: 'Chargement des derniers avis…',
    error: 'Impossible de charger les avis pour le moment.',
    unavailable: 'indisponible',
    destinations: 'destinations',
    empty: 'Aucun avis ne correspond à ce filtre.',
    all: 'Tous',
    level: 'Niveau',
    read: 'Lire l’avis →',
    consensus: (n) => 'Consensus ' + n + ' MAE',
    levels: {
      1: 'Vigilance normale',
      2: 'Vigilance renforcée',
      3: 'Déconseillé sauf raison impérative',
      4: 'Formellement déconseillé',
    },
    sources: { US: 'MAE États-Unis', CA: 'MAE Canada', DE: 'MAE Allemagne', GB: 'MAE Royaume-Uni', FR: 'MAE France' },
  },
};

const MOUNTED = '__vmAdvMounted';

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}

function fmtDate(d, lang) {
  const t = typeof d === 'number' ? d : Date.parse(d);
  if (!t) return '';
  return new Date(t).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function countryName(iso2, lang) {
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(iso2) || iso2;
  } catch {
    return iso2;
  }
}

function flag(iso2, name) {
  if (!iso2) return '<span class="vma__flag vma__flag--none" aria-hidden="true"></span>';
  const lc = iso2.toLowerCase();
  return (
    '<img class="vma__flag" loading="lazy" width="38" height="38"' +
    ' src="https://flagcdn.com/w40/' + lc + '.png"' +
    ' srcset="https://flagcdn.com/w80/' + lc + '.png 2x"' +
    ' alt="' + esc(name) + '" />'
  );
}

function originBadge(row, t) {
  const lvl = row.level || 0;
  const lc = row.source.toLowerCase();
  const label = t.sources[row.source] || row.source;
  const inner =
    '<img loading="lazy" width="16" height="16" src="https://flagcdn.com/w40/' + lc + '.png" alt="" />' +
    row.source +
    '<span class="vma__origin-l" style="--fill:var(--lvl-' + lvl + ')">' + (row.level == null ? '—' : row.level) + '</span>';
  return row.url
    ? '<a class="vma__origin" href="' + esc(row.url) + '" target="_blank" rel="noopener" title="' + esc(label) + '">' + inner + '</a>'
    : '<span class="vma__origin" title="' + esc(label) + '">' + inner + '</span>';
}

function card(g, lang, t, consensusOf) {
  const name = countryName(g.iso2, lang);
  const label = t.levels[g.level] || '';
  // Prefer the per-destination synthesis (one paragraph across all ministries);
  // fall back to the headline ministry's own text when none exists yet.
  const synthesis = g.summary && g.summary.text ? g.summary.text[lang] || '' : '';
  const summary = synthesis || (g.top && g.top.summary ? g.top.summary[lang] || '' : '');
  const date = g.latest ? fmtDate(g.latest, lang) : '';
  return (
    '<li class="vma__item" data-level="' + g.level + '" style="--fill:var(--lvl-' + g.level + ')">' +
      '<div class="vma__head">' + flag(g.iso2, name) +
        '<span class="vma__country">' + esc(name) + '</span>' +
        (date ? '<time class="vma__date">' + esc(date) + '</time>' : '') +
      '</div>' +
      '<span class="vma__level">' +
        '<span class="vma__level-n">' + t.level + ' ' + (g.level || '—') + '</span>' +
        (label ? '<span class="vma__level-label">' + esc(label) + '</span>' : '') +
      '</span>' +
      '<div class="vma__origins">' +
        g.rows.map((r) => originBadge(r, t)).join('') +
        (consensusOf ? '<span class="vma__consensus">' + t.consensus(consensusOf) + '</span>' : '') +
      '</div>' +
      (summary ? '<p class="vma__summary' + (synthesis ? ' vma__summary--synthesis' : '') + '">' + esc(summary) + '</p>' : '') +
      (g.top && g.top.url
        ? '<a class="vma__read" href="' + esc(g.top.url) + '" target="_blank" rel="noopener">' + t.read + '</a>'
        : '') +
    '</li>'
  );
}

function skeleton(t, intro) {
  return (
    '<header class="vma__bar"><div>' +
      '<h2 class="vma__title">' + t.title + '</h2>' +
      '<p class="vma__src">' + esc(intro) + ' · <span data-updated>' + t.loading + '</span></p>' +
    '</div>' +
    '<div class="vma__filters" role="group" aria-label="Filter by level" hidden>' +
      '<button type="button" class="vma__chip" data-level="all">' + t.all + '</button>' +
      '<button type="button" class="vma__chip" data-level="1"><span class="vma__dot" data-l="1"></span>&ge;&hairsp;1</button>' +
      '<button type="button" class="vma__chip" data-level="2"><span class="vma__dot" data-l="2"></span>&ge;&hairsp;2</button>' +
      '<button type="button" class="vma__chip" data-level="3"><span class="vma__dot" data-l="3"></span>&ge;&hairsp;3</button>' +
      '<button type="button" class="vma__chip" data-level="4"><span class="vma__dot" data-l="4"></span>4</button>' +
    '</div></header>' +
    '<p class="vma__status" data-status>' + t.loading + '</p>' +
    '<ul class="vma__list" data-list></ul>'
  );
}

export function mount(host) {
  if (!host || host[MOUNTED]) return;
  host[MOUNTED] = true;

  const endpoint = host.dataset.endpoint || DEFAULT_ENDPOINT;
  const limit = Number(host.dataset.limit) || 24;
  const theme = host.dataset.theme === 'dark' ? 'dark' : 'light';
  const lang = host.dataset.lang === 'fr' ? 'fr' : 'en';
  const sourceRaw = (host.dataset.source || '').toUpperCase();
  const source = SOURCE_ORDER.includes(sourceRaw) ? sourceRaw : '';
  const minLevel = ['1', '2', '3', '4'].includes(host.dataset.minLevel) ? host.dataset.minLevel : 'all';
  const consensus = host.dataset.consensus != null && host.dataset.consensus !== 'false';
  const t = I18N[lang];
  const intro = source ? t.introSingle + ' ' + t.sources[source] : t.intro;

  const root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
  const style = document.createElement('style');
  style.textContent = styles;
  root.appendChild(style);

  const wrap = document.createElement('div');
  wrap.className = 'vma';
  wrap.setAttribute('data-theme', theme);
  wrap.innerHTML = skeleton(t, intro);
  root.appendChild(wrap);

  const listEl = wrap.querySelector('[data-list]');
  const statusEl = wrap.querySelector('[data-status]');
  const updatedEl = wrap.querySelector('[data-updated]');
  const filtersEl = wrap.querySelector('.vma__filters');
  let groups = [];
  // Coverage per destination across ALL ministries — consensus is judged on
  // the full picture even when data-source narrows what is displayed.
  let coverage = new Map();
  let active = minLevel;

  function markActiveChip() {
    filtersEl.querySelectorAll('.vma__chip').forEach((c) => c.classList.toggle('is-active', c.dataset.level === active));
  }

  // Consensus bar: the active "≥ N" level, never below 2 (level-1 unanimity is
  // the norm, not a signal).
  function consensusOf(iso2) {
    const bar = Math.max(2, Number(active) || 2);
    const rows = coverage.get(iso2) || [];
    return rows.length >= 4 && rows.every((r) => (r.level ?? 0) >= bar) ? rows.length : 0;
  }

  function render() {
    markActiveChip();
    const lvl = active === 'all' ? 0 : Number(active);
    let filtered = lvl ? groups.filter((g) => g.level >= lvl) : groups;
    if (consensus) filtered = filtered.filter((g) => consensusOf(g.iso2));
    const shown = filtered.slice(0, limit);
    listEl.innerHTML = shown.map((g) => card(g, lang, t, consensus ? consensusOf(g.iso2) : 0)).join('');
    if (shown.length === 0) {
      statusEl.hidden = false;
      statusEl.textContent = t.empty;
    } else {
      statusEl.hidden = true;
    }
  }

  fetch(endpoint, { headers: { Accept: 'application/json' } })
    .then((r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .then((data) => {
      const rows = (data.advisories || []).filter((a) => a.iso2);
      coverage = new Map();
      for (const a of rows) {
        if (!coverage.has(a.iso2)) coverage.set(a.iso2, []);
        coverage.get(a.iso2).push(a);
      }
      groups = groupByDestination(source ? rows.filter((a) => a.source === source) : rows);
      const summaries = data.summaries || {};
      for (const g of groups) g.summary = summaries[g.iso2] || null;
      if (updatedEl) updatedEl.textContent = groups.length + ' ' + t.destinations;
      filtersEl.hidden = false;
      render();
    })
    .catch(() => {
      statusEl.classList.add('is-error');
      statusEl.textContent = t.error;
      if (updatedEl) updatedEl.textContent = t.unavailable;
    });

  filtersEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-level]');
    if (!btn) return;
    active = btn.dataset.level;
    render();
  });
}

export function mountAll(root = document) {
  root.querySelectorAll('[data-visamundi-advisories]').forEach((el) => mount(el));
}

function autoInit() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
  } else {
    mountAll();
  }
}

const api = { mount, mountAll };
if (typeof window !== 'undefined') {
  window.VisamundiAdvisories = api;
  autoInit();
}

export default api;
